/**
 * 买家端商品 API（对接真实后端 /api/buyer/product/**）
 * 前端适配版：base path 改为 /buyer/product，接口契约与 admin product.ts 对齐。
 *
 * 注意：买家端请求封装（@/api/request）暴露的是命名导出 get/post/put/del，
 * 没有 admin 那样的默认 request 函数，故此处与 inquiry.ts 保持一致使用命名导入。
 */
import { get } from '@/api/request';
import type { PageResult } from '@bluplanet/shared';

/** 商品列表查询参数 */
export interface ProductQuery {
  page: number;
  pageSize?: number;
  name?: string;
  categoryId?: string;
  /** 逗号分隔的分类 ID 列表（含自身及后代），优先于 categoryId */
  categoryIds?: string;
}

/**
 * 买家端商品 VO（与后端 BuyerProductVO 对齐）。
 * 后端已带出当前挂牌价，前端无需硬编码兜底；currentFobPrice 为 FOB 参考挂牌价(CNY)。
 */
export interface BuyerProductVO {
  id: string;
  name: string;
  productCode?: string;
  hsCode?: string;
  specification?: string;
  packagingSpec?: string;
  categoryId?: string;
  categoryName?: string;
  description?: string;
  /** 封面图 URL（买家端卡片展示；空则前端回退 emoji 渐变） */
  coverImageUrl?: string | null;
  /** 商品图集 URL（JSON 数组字符串；前端解析为数组轮播） */
  imageUrls?: string | null;
  /** 最新行情价（CNY） */
  currentPrice?: number | null;
  /** FOB 参考挂牌价（CNY） */
  currentFobPrice?: number | null;
  /** 计价单位 */
  priceUnit?: string;
  /** 默认箱型（由商品储运属性决定，如 REEFER_20） */
  defaultContainerType?: string | null;
  /** 默认箱型每柜装载量（吨） */
  defaultContainerLoadQty?: number | null;
}

/** 商品搜索结果VO（与 admin product.ts 保持一致） */
export interface ProductSearchVO {
  id: string;
  productCode: string;
  name: string;
  categoryId: string;
  categoryName: string;
  specification: string;
  packagingSpec: string;
  standardPrice: number;
  /** 采购成本（来自 product_listings.cost_price，订单采购单价真实来源） */
  costPrice?: number;
  unit: string;
  supplierId: string;
  supplierName: string;
}

/**
 * SPU（商品级）公开 VO —— 对齐后端 ProductSpuVO（公开接口 /product-spu/public/*）。
 * 买家端按 SPU 浏览：SPU 承载介绍/图文/产地/合规，skus 为该 SPU 下的可选规格（SKU）。
 */
export interface BuyerSkuVO {
  /** = product.id，下单/询盘时携带此 id（交易粒度=SKU） */
  id: string;
  productCode?: string;
  name: string;
  categoryId?: string;
  /** 品种（SKU 维度，如 纯白/紫皮） */
  variety?: string;
  /** 品种英文名（EN 模式展示；空则回退中文） */
  varietyEn?: string;
  /** 规格（如 5.5+） */
  specification?: string;
  /** 规格英文展示（仅与中文不同时填；空则回退中文） */
  specificationEn?: string;
  /** 包装规格（如 10kg/箱） */
  packagingSpec?: string;
}

export interface BuyerSpuVO {
  id: string;
  categoryId?: string;
  /** 分类名称（后端已解析，列表/详情统一带出） */
  categoryName?: string;
  /** 分类英文名（EN 模式展示；空则回退中文） */
  categoryNameEn?: string;
  /** 品类封面图（SPU 无封面时前端回退用） */
  categoryCoverImageUrl?: string | null;
  name: string;
  /** SPU 英文名（EN 模式展示；空则回退中文） */
  nameEn?: string;
  hsCode?: string;
  description?: string;
  /** SPU 英文富文本介绍（EN 模式展示；空则回退中文） */
  descriptionEn?: string;
  coverImageUrl?: string | null;
  /** 商品图集 URL（JSON 数组字符串） */
  imageUrls?: string | null;
  /** 该 SPU 下 SKU 数量 */
  skuCount?: number;
  /** 行情裸价区间（CNY/吨，后端聚合）：全缺时为 null */
  priceMin?: number | null;
  priceMax?: number | null;
  /** 该 SPU 下的可选规格（SKU 列表） */
  skus?: BuyerSkuVO[];
}

/** 商品列表（分页，带价）：GET /buyer/product/list */
export function listProducts(params: ProductQuery) {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  if (params.pageSize != null) qs.set('pageSize', String(params.pageSize));
  if (params.name) qs.set('name', params.name);
  if (params.categoryId) qs.set('categoryId', params.categoryId);
  if (params.categoryIds) qs.set('categoryIds', params.categoryIds);
  return get<PageResult<BuyerProductVO>>(`/buyer/product/list?${qs.toString()}`);
}

/** 商品搜索（用于订单创建）：GET /buyer/product/search?keyword=xxx */
export function searchProducts(keyword: string) {
  return get<ProductSearchVO[]>(`/buyer/product/search?keyword=${encodeURIComponent(keyword)}`);
}

/** 商品详情（带价）：GET /buyer/product/{id} */
export function getProduct(id: string) {
  return get<BuyerProductVO>(`/buyer/product/${id}`);
}

// ===================== SPU（商品级）公开接口 =====================
// 买家端按 SPU 浏览：/api/product-spu/public/* 由网关路由到 blu-trade（需在网关加 /api/product-spu/** 路由）。
const SPU_PUBLIC = '/product-spu/public';

/** SPU 分页列表（买家端公开，跨租户）：GET /product-spu/public/list */
/** 首页 SPU 列表短期缓存：同参数 30s 内复用同一 Promise，避免多组件/dev StrictMode 重复请求 */
const listSpusCache = new Map<string, { at: number; p: Promise<PageResult<BuyerSpuVO>> }>();
const LIST_SPUS_TTL_MS = 30_000;

function listCatalog(params: ProductQuery, endpoint: 'list' | 'cards') {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  if (params.pageSize != null) qs.set('pageSize', String(params.pageSize));
  if (params.name) qs.set('name', params.name);
  if (params.categoryId) qs.set('categoryId', params.categoryId);
  // 透传当前 host：后端按它生成与浏览器实际访问 MinIO 一致的预签名 URL
  if (typeof window !== 'undefined' && window.location.hostname) {
    qs.set('host', window.location.hostname);
  }
  const key = `${endpoint}?${qs.toString()}`;

  // 注意：SPU 接口仅支持单 categoryId 过滤，categoryIds（逗号列表）不传给后端
  const cached = listSpusCache.get(key);
  const fresh = cached && Date.now() - cached.at < LIST_SPUS_TTL_MS;
  if (fresh) return cached!.p;

  const p = get<PageResult<BuyerSpuVO>>(`${SPU_PUBLIC}/${key}`).catch((err) => {
    listSpusCache.delete(key); // 失败不缓存，下次重试
    throw err;
  });
  listSpusCache.set(key, { at: Date.now(), p });
  return p;
}

export const listSpus = (params: ProductQuery) => listCatalog(params, 'list');
export const listCards = (params: ProductQuery) => listCatalog(params, 'cards');
export interface CatalogCategory { id: string; name: string; nameEn?: string; imageUrl?: string }
let categoriesCache: { at: number; p: Promise<CatalogCategory[]> } | undefined;
export function publicCategories() {
  if (categoriesCache && Date.now() - categoriesCache.at < LIST_SPUS_TTL_MS) return categoriesCache.p;
  const host = typeof window === 'undefined' ? '' : window.location.hostname;
  const p = get<CatalogCategory[]>(`${SPU_PUBLIC}/categories?host=${encodeURIComponent(host)}`).catch(error => {
    categoriesCache = undefined;
    throw error;
  });
  categoriesCache = { at: Date.now(), p };
  return p;
}

/** SPU 详情（含 SKU 列表）：GET /product-spu/public/{id} */
export function getSpu(id: string) {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const qs = host ? `?host=${encodeURIComponent(host)}` : '';
  return get<BuyerSpuVO>(`${SPU_PUBLIC}/${id}${qs}`);
}

/** 港口字典选项（买家端公开，免登录；kb_port_code 平台级字典） */
export interface PortOption {
  portCode: string;
  portNameCn: string;
  portNameEn?: string;
  countryCode: string;
  countryNameCn: string;
  isMainPort?: boolean;
}

/** 港口字典全量选项：GET /product-spu/public/port-options（询盘页按目的国本地过滤） */
export function listPublicPortOptions() {
  return get<PortOption[]>('/product-spu/public/port-options');
}

/** 港口字典模块级缓存（Promise 级）：询盘页/首页信任数字带共用，整个会话只拉一次；失败清缓存可重试 */
let portOptionsCache: Promise<PortOption[]> | null = null;
export function listPublicPortOptionsCached(): Promise<PortOption[]> {
  if (!portOptionsCache) {
    portOptionsCache = get<PortOption[]>('/product-spu/public/port-options').catch((e) => {
      portOptionsCache = null;
      throw e;
    });
  }
  return portOptionsCache;
}

export const productApi = {
  list: listProducts,
  search: searchProducts,
  getById: getProduct,
  listSpus,
  listCards,
  publicCategories,
  getSpu,
  listPublicPortOptions,
  listPublicPortOptionsCached,
};

export default productApi;
